import UAParser from 'ua-parser-js';

const OVERRIDE_STORAGE_KEY = 'ih:device-always-valid';
const OVERRIDE_QUERY_PARAM = '__device-always-valid';

export interface IDeviceInfo {
    type: string;
    vendor: string;
    browserName: string;
    browserVersion: number;
    ua: string;
}

export type IDeviceValidation = {
    validDevice: boolean;
    validBrowser: boolean;
    deviceInfo: IDeviceInfo;
};

type ISupportedBrowser = {
    name: string;
    minVersion: number
};

type ISupportedDevice = {
    type: 'desktop' | 'tablet' | 'mobile';
    vendor?: string;
}

export class DeviceValidator {

    constructor() {
        const searchParams = new URLSearchParams(window.location.search);
        const value = searchParams.get(OVERRIDE_QUERY_PARAM) || '';
        if (['true', 'false'].includes(value)) {
            window.localStorage.setItem(OVERRIDE_STORAGE_KEY, value);
        }
    }

    private parser = new UAParser();
    private deviceAlwaysValid = (window.localStorage.getItem(OVERRIDE_STORAGE_KEY) === 'true');

    private supportedDevices: ISupportedDevice[] = [
        { type: 'desktop' },
        { type: 'mobile' },
        { type: 'tablet', vendor: 'Apple' }
    ];

    private supportedBrowsers: ISupportedBrowser[] = [{
        name: 'Chrome',
        minVersion: 115,
    },{
        name: 'Firefox',
        minVersion: 115,
    },{
        name: 'Safari',
        minVersion: 15,
    },{
        name: 'Mobile Safari', // iPad mini and below. iPad apparently uses 'Safari'
        minVersion: 15,
    },{
        name: 'Edge',
        minVersion: 113,
    },{
        // Electron-based webviews (e.g. desktop app shells, embedded dev/QA browsers) report as
        // "Electron" even though they run a current Chromium. Electron 20 ~= Chromium 104, so any
        // recent Electron is compatible.
        name: 'Electron',
        minVersion: 20,
    }];

    // returns all the device info we use for validation
    private getDeviceInfo = () => {
        const device = this.parser.getDevice();
        const browser = this.parser.getBrowser();
        return {
            type: (device.type || 'desktop'), // parser returns desktop as undefined
            vendor: device.vendor || '',
            browserName: browser.name || '',
            browserVersion: Number(browser.version?.split('.')[0]), // could be NaN
            ua: this.parser.getUA()
        };
    };

    private isSupportedDevice = (deviceInfo: IDeviceInfo): boolean => {
        if (this.deviceAlwaysValid) {
            return true;
        }
        if (this.supportedDevices.find(d => d.type === deviceInfo.type && d.vendor === deviceInfo.vendor)) {
            return true;
        }
        if (this.supportedDevices.find(d => d.type === deviceInfo.type && d.vendor === undefined)) {
            return true;
        }
        return false;
    };

    private isSupportedBrowser = (deviceInfo: IDeviceInfo): boolean => {
        if (this.deviceAlwaysValid) {
            return true;
        }
        if (this.supportedBrowsers.find(b => b.name === deviceInfo.browserName && b.minVersion <= deviceInfo.browserVersion)) {
            return true;
        }
        return false;
    };

    validate = (): IDeviceValidation => {
        const deviceInfo = this.getDeviceInfo();
        return {
            validDevice: this.isSupportedDevice(deviceInfo),
            validBrowser: this.isSupportedBrowser(deviceInfo),
            deviceInfo
        };
    };
};
